import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { AFFILIATE, PHYSICIAN_TEAM } from 'constants/locationTypes';
import { STATE_OPTIONS } from 'constants/states';
import { TIMEZONES } from 'constants/timezones';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { ClientType } from 'models/flexible/Client';
import { GroupType } from 'models/flexible/GroupType';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminGroupsQueryKeys, adminShowGroup, createGroup, updateGroup } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import { adminIndexLocations } from 'services/api/admin/locations';

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

const baseSchema = yup.object({
  name: yup.string().required('Group name is required'),
  client: yup.mixed().test('client', 'Client is required', (v: any) => !!v?.id),
  groupType: yup.mixed().test('groupType', 'Group type is required', (v: any) => !!v?.id),
  timezone: yup.mixed().test('timezone', 'Timezone is required', (v: any) => !!v?.value),
  location: yup.mixed().test('location', 'Location is required', (v: any) => !!v?.id),
});

function needsAddress(groupType: GroupType | null): boolean {
  if (!groupType?.apiName) return true;
  return ![PHYSICIAN_TEAM, AFFILIATE].includes(groupType.apiName);
}

export default function EditGroup() {
  const { id } = useParams();
  const isEditing = !!id;
  const { profile } = useProfile();

  const form = useAdminForm({
    id,
    show: adminShowGroup,
    create: createGroup,
    update: updateGroup,
    include: ['groupType', 'networkClients', 'location'] as const,
    queryKey: adminGroupsQueryKeys,
    schema: baseSchema,
    toFormValues: (data) => ({
      name: data?.name ?? '',
      client: data?.client?.id ? data.client : null,
      groupType: data?.groupType?.id ? new GroupType(data.groupType) : null,
      streetAddress: data?.address?.streetAddress ?? '',
      streetAddressLine2: data?.address?.streetAddressLine2 ?? '',
      city: data?.address?.city ?? '',
      state: data?.address?.state
        ? { label: data.address.state, value: data.address.state }
        : (null as { label: string; value: string } | null),
      zip: data?.address?.zip ?? '',
      timezone: data?.timezone
        ? (TIMEZONES.find((tz) => tz.value === data.timezone) ?? null)
        : (null as { label: string; value: string } | null),
      networkClients: data?.networkClients ?? [],
      location: data?.location?.id ? data.location : (null as { id: string; name: string } | null),
      externalId: data?.externalId ?? '',
    }),
    toPayload: (v) => ({
      name: v.name ?? '',
      clientId: v.client?.id,
      clientType: v.client?.clientType,
      groupTypeId: v.groupType?.id ?? '',
      timezone: v.timezone?.value ?? '',
      externalId: v.externalId ?? '',
      locationId: v.location?.id || undefined,
      networkClientIds: (v.networkClients ?? []).map((c: any) => c.id),
      address: {
        streetAddress: v.streetAddress ?? '',
        streetAddressLine_2: v.streetAddressLine2 ?? '',
        streetAddressLine2: v.streetAddressLine2 ?? '',
        city: v.city ?? '',
        state: v.state?.value ?? '',
        zip: v.zip ?? '',
      },
    }),
    onCreateSuccess: { toast: 'Group successfully added!', navigate: '/admin/groups' },
    onUpdateSuccess: { toast: 'Group successfully updated!', navigate: '/admin/groups' },
  });

  const canEdit = profile.has(PERMISSIONS.adminGroupEdit);
  const disableClient = isEditing || !canEdit;
  const disableGroupType = isEditing || !canEdit;
  const showAddress = needsAddress(form.values.groupType ?? null);

  const clientParams = useMemo(() => {
    if (isEditing && form.data?.client?.clientType) {
      return { clientType: form.data!.client.clientType };
    }
    return {};
  }, [isEditing, form.data]);

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'groups', clientParams],
    fetchFn: adminIndexClients,
    params: { ...clientParams, sortBy: 'name asc' },
  });

  const groupTypeOpts = useInfiniteOptions({
    queryKey: ['groupTypes', 'all'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc' },
  });

  const associatedClientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'associated', form.values.client?.id],
    fetchFn: adminIndexClients,
    params: {
      leaf: true,
      'clientType.not': ClientType.ADMIN,
      'id.not': form.values.client?.id,
      sortBy: 'name asc',
    },
  });

  const locationOpts = useInfiniteOptions({
    queryKey: ['adminLocations', 'forGroup', form.values.client?.id],
    fetchFn: adminIndexLocations,
    params: { clientId: form.values.client?.id, sortBy: 'name asc' },
  });

  if (isEditing && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <>
        <FormHeader title={`${isEditing ? 'Edit' : 'Add'} Group`} />

        <SectionHeader>Contact Information</SectionHeader>

        <InputGroup title='Client' error={form.errors.client}>
          <Select
            {...clientOpts}
            value={form.values.client}
            onChange={(v) => {
              form.setValue('client', v);
              if (!isEditing) {
                form.setValue('groupType', null, { validate: false });
                form.setValue('location', null, { validate: false });
              }
            }}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Client'
            isDisabled={!!disableClient}
          />
        </InputGroup>

        <InputGroup title='Group Name' error={form.errors.name}>
          <Input
            value={form.values.name ?? ''}
            onChange={(e) => form.setValue('name', e.target.value)}
            placeholder='Group Name'
            $hasError={!!form.errors.name}
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='Group Type' error={form.errors.groupType}>
          <Select
            {...groupTypeOpts}
            value={form.values.groupType}
            onChange={(v) => form.setValue('groupType', v)}
            getOptionValue={getId}
            getOptionLabel={getDisplayName}
            placeholder='Group Type'
            isDisabled={disableGroupType}
          />
        </InputGroup>

        <InputGroup title='Location' error={form.errors.location}>
          <Select
            {...locationOpts}
            value={form.values.location}
            onChange={(v) => form.setValue('location', v)}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Search for a location...'
            isDisabled={!form.values.client?.id || !canEdit}
          />
        </InputGroup>

        {showAddress && (
          <>
            <InlineGroup>
              <InputGroup title='Street 1' error={form.errors.streetAddress}>
                <Input
                  value={form.values.streetAddress ?? ''}
                  onChange={(e) => form.setValue('streetAddress', e.target.value)}
                  placeholder='Street 1'
                  $hasError={!!form.errors.streetAddress}
                  disabled={!canEdit}
                />
              </InputGroup>
              <InputGroup title='Street 2 (optional)'>
                <Input
                  value={form.values.streetAddressLine2 ?? ''}
                  onChange={(e) => form.setValue('streetAddressLine2', e.target.value)}
                  placeholder='Street 2'
                  disabled={!canEdit}
                />
              </InputGroup>
            </InlineGroup>

            <InlineGroup>
              <InputGroup title='City' error={form.errors.city}>
                <Input
                  value={form.values.city ?? ''}
                  onChange={(e) => form.setValue('city', e.target.value)}
                  placeholder='City'
                  $hasError={!!form.errors.city}
                  disabled={!canEdit}
                />
              </InputGroup>
              <InlineGroup>
                <InputGroup title='State' error={form.errors.state}>
                  <Select
                    options={STATE_OPTIONS}
                    value={form.values.state}
                    onChange={(v) => form.setValue('state', v)}
                    getOptionValue={(o) => o.value}
                    getOptionLabel={(o) => o.label}
                    placeholder='State'
                    isDisabled={!canEdit}
                  />
                </InputGroup>
                <InputGroup title='Zip' error={form.errors.zip}>
                  <Input
                    value={form.values.zip ?? ''}
                    onChange={(e) => form.setValue('zip', e.target.value)}
                    placeholder='Zip'
                    $hasError={!!form.errors.zip}
                    disabled={!canEdit}
                  />
                </InputGroup>
              </InlineGroup>
            </InlineGroup>
          </>
        )}

        <InputGroup title='Timezone' error={form.errors.timezone}>
          <Select
            options={TIMEZONES}
            value={form.values.timezone}
            onChange={(v) => form.setValue('timezone', v)}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            placeholder='Timezone'
            isDisabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Associated Clients</SectionHeader>

        <InputGroup>
          <MultiSelectChips
            title='Associated Clients'
            {...associatedClientOpts}
            values={form.values.networkClients}
            onChange={(v) => form.setValue('networkClients', v)}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Associated Clients'
          />
        </InputGroup>

        <SectionHeader>Integrations</SectionHeader>

        <InputGroup title='MatrixCare Facility ID (optional)'>
          <Input
            value={form.values.externalId ?? ''}
            onChange={(e) => form.setValue('externalId', e.target.value)}
            placeholder='Facility ID'
            disabled={!canEdit}
          />
        </InputGroup>

        <div style={{ display: 'flex', width: '100%' }}>
          {!isEditing && (
            <Button
              variant='outline-primary'
              onClick={() => form.submitAndReset(['name'])}
              disabled={!form.valid || form.submitting}
              loading={form.submitting}>
              Save and Add Another
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={form.goBack}>
              Cancel
            </Button>
            <Button onClick={form.submit} disabled={!form.valid || form.submitting} loading={form.submitting}>
              {isEditing ? 'Update Group' : 'Create Group'}
            </Button>
          </ButtonGroup>
        </div>
      </>
    </AdminFormPage>
  );
}

const InlineGroup = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
